<script lang="ts">
    import { DEFAULT_BOX } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { getColorByLightness } from '$lib/helper';
    import { cn } from '$lib/utils/cn';

    import Transition from '../_wrappers/Transition.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { TransitionEffect } from '$lib/types/Theme';
    import type { ResolvedAccordionItemStyling } from './_utils/resolveAccordionItemStyling';

    type TextType = {
        color?: string;
        wysiwygHtml: string;
    };

    export let title: TextType;
    export let body: TextType;
    export let bgColor: string | undefined = '#f5f5f5';
    export let transitionEffect: TransitionEffect = 'default';
    export let transitionDelay = 0;
    export let styling: ResolvedAccordionItemStyling | undefined = undefined;

    let isOpen = false;
    const uniqueId = Math.random().toString(36).substring(2, 11);
    let bodyId = `accordion-body-${uniqueId}`;
    let bodyElem: HTMLDivElement;
    let svgClasses: string;
    let bodyClasses: string;

    const handleToggle = () => {
        if (isOpen) {
            bodyElem.style.maxHeight = '0';
        } else {
            bodyElem.style.maxHeight = `${bodyElem.scrollHeight}px`;
        }

        isOpen = !isOpen;
    };

    $: svgClasses = cn(
        'fun-absolute fun-right-0 fun-top-0 fun-h-6 fun-w-6 fun-transform fun-transition fun-duration-[400ms] fun-ease-[cubic-bezier(0.16,1,0.3,1)] md:fun-right-2 md:fun-top-2',
        {
            'fun-rotate-0': !isOpen,
            'fun--rotate-180': isOpen,
        },
    );

    $: bodyClasses = cn(
        'fun-transform fun-overflow-hidden fun-px-4 fun-transition-all fun-duration-[400ms] fun-ease-[cubic-bezier(0.16,1,0.3,1)]',
        {
            'fun-max-h-0 fun--translate-y-2 fun-opacity-0': !isOpen,
            'fun-translate-y-0 fun-opacity-100': isOpen,
        },
    );

    $: wrapperClasses = cn(
        styling?.borderRadiusClass ?? getBorderRadius('faqItem'),
        styling?.shadowClass,
        styling?.borderWidthClass,
        styling?.borderStyleClass,
    );

    $: wrapperSurface = styling?.surfaceColor ?? bgColor;
</script>

<Transition effect={transitionEffect} delay={transitionDelay}>
    <div
        class={wrapperClasses}
        style:background-color={wrapperSurface || null}
        style:border-color={styling?.borderColor || null}
    >
        <div class="fun-relative md:fun-p-2">
            <button
                on:click={handleToggle}
                class="fun-flex fun-w-full fun-cursor-pointer fun-px-4 fun-py-4"
            >
                <Text
                    wide
                    wysiwygHtml={title.wysiwygHtml}
                    color={title.color}
                    box={DEFAULT_BOX}
                    transitionEffect="none"
                />
                <div class="fun-relative fun-h-6 fun-w-6">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class={svgClasses}
                        viewBox="0 0 20 20"
                        fill={getColorByLightness(wrapperSurface ?? '#f5f5f5')}
                        aria-hidden="true"
                    >
                        <path
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        />
                    </svg>
                </div>
            </button>
            <div id={bodyId} class={bodyClasses} bind:this={bodyElem}>
                <Text
                    wide
                    wysiwygHtml={body.wysiwygHtml}
                    color={body.color}
                    box={DEFAULT_BOX}
                    transitionEffect="none"
                />
                <div class="fun-py-2" />
            </div>
        </div>
    </div>
</Transition>
